import { useId, useState } from 'react';
import { computeFlowSankeyLayout, visibleColLabelCols, type FlowSankeySpec } from './flowSankeyLayout';

/**
 * SIL-516/SIL-517 — reusable hand-rolled-SVG Sankey, ported from a validated
 * prototype (design-eval artifact linked from LifecycleSankey.tsx, the
 * first consumer). Not recharts — this portal's charts that need a shape
 * recharts doesn't have (ribbons, multi-stage flow) are hand-drawn SVG, same
 * convention as the shipped single-stage `LifecycleSankey` this replaces.
 *
 * Layout math lives in `flowSankeyLayout.ts` (pure, unit-tested); this file
 * is rendering only: responsive viewBox, cubic-bezier ribbons, hover
 * tooltip + link/node dimming, an optional dashed "quarantine" boundary
 * around nodes flagged `quarantine` (SIL-459 — the advisory boundary is
 * drawn IN the diagram; it does not replace an outer quarantine panel a
 * caller may already wrap this in), and a striped fill for nodes/links
 * flagged `striped` (this component never decides what striping means —
 * callers key it off their own basis field, e.g. `basis === 'sample'`).
 *
 * Kept generic on purpose: `colPos`/`colLabels` support time-proportional
 * columns (a later consumer places a column at a specific month fraction
 * rather than even spacing) even though the first consumer doesn't use it.
 */
export interface FlowSankeyProps extends FlowSankeySpec {
  /** Formats a raw numeric value for node sub-labels and tooltips. */
  format: (value: number) => string;
  ariaLabel: string;
  /** Column id -> header text, rendered along the top (time axes). */
  colLabels?: Record<number, string>;
  /** Rendered once inside the SVG defs+content, useful for a caller-owned
   * legend key; purely additive, never required. */
  className?: string;
}

export function FlowSankey({ format, ariaLabel, colLabels, className, ...spec }: FlowSankeyProps) {
  const layout = computeFlowSankeyLayout(spec);
  const stripeId = useId();
  const [hoverLinkIndex, setHoverLinkIndex] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const quarantineNodes = layout.nodes.filter((n) => n.quarantine);
  let quarantineBox: { x: number; y: number; width: number; height: number } | null = null;
  if (quarantineNodes.length > 0) {
    const qy0 = Math.min(...quarantineNodes.map((n) => n.y0)) - 14;
    const qy1 = Math.max(...quarantineNodes.map((n) => n.y1)) + 8;
    const qx0 = Math.min(...quarantineNodes.map((n) => n.x0)) - 8;
    quarantineBox = { x: qx0, y: qy0, width: layout.width - qx0 - 2, height: qy1 - qy0 };
  }

  const showTooltip = (e: React.MouseEvent, text: string) => {
    setTooltip({ x: e.clientX, y: e.clientY, text });
  };

  // SIL — keyboard/screen-reader equivalent for the tooltip-only data: each
  // focusable node shows the same tooltip on focus as on hover (positioned
  // from its own bounding box, since a focus event carries no pointer
  // coordinates), and gets a visible focus ring — the emerald ring is this
  // app's entire focus expression (DESIGN.md), never dropped.
  const showTooltipAt = (e: React.FocusEvent<SVGElement>, text: string) => {
    const box = e.currentTarget.getBoundingClientRect();
    setTooltip({ x: box.left, y: box.bottom, text });
  };
  const FOCUS_RING =
    'outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500';

  return (
    <div className={`relative ${className ?? ''}`}>
      <svg
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        role="img"
        aria-label={ariaLabel}
        className="h-auto w-full"
        onMouseMove={(e) => setTooltip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t))}
      >
        <defs>
          <pattern id={`stripe-${stripeId}`} width="7" height="7" patternTransform="rotate(-45)" patternUnits="userSpaceOnUse">
            <rect width="7" height="7" className="fill-sky-500" />
            <rect width="3.5" height="7" className="fill-sky-300" />
          </pattern>
        </defs>

        {colLabels &&
          visibleColLabelCols(layout.cols, layout.colX, colLabels).map((c) => {
            const label = colLabels[c];
            if (!label) return null;
            const isFirst = c === layout.cols[0];
            const x = isFirst ? 4 : (layout.colX.get(c) ?? 0) + 5;
            return (
              <text
                key={`collabel-${c}`}
                x={x}
                y={12}
                textAnchor={isFirst ? 'start' : 'middle'}
                className="fill-muted-foreground text-xs font-medium tracking-wide"
              >
                {label}
              </text>
            );
          })}

        {quarantineBox && (
          <g>
            <rect
              x={quarantineBox.x}
              y={quarantineBox.y}
              width={quarantineBox.width}
              height={quarantineBox.height}
              rx={6}
              className="fill-sky-50 stroke-sky-300"
              strokeWidth={1.2}
              strokeDasharray="4 3"
            />
            <text
              x={layout.width - 10}
              y={quarantineBox.y + 11}
              textAnchor="end"
              className="fill-sky-700 text-xs font-semibold uppercase tracking-wider"
            >
              Advisory · quarantined
            </text>
          </g>
        )}

        {layout.links.map((l, i) => {
          const isHovered = hoverLinkIndex === i;
          const isDimmed = hoverLinkIndex !== null && !isHovered;
          const tipText = l.tip ?? `${l.source} → ${l.target}\n${format(l.value)}${l.negligible ? ' (≈0 — rendered at the floor)' : l.floored ? ' (thin flow — rendered at minimum thickness)' : ''}`;
          return (
            <path
              key={`link-${l.source}-${l.target}-${i}`}
              d={l.path}
              fill={l.striped ? `url(#stripe-${stripeId})` : undefined}
              className={`${l.striped ? '' : l.fillClass} ${FOCUS_RING}`}
              opacity={isDimmed ? 0.12 : isHovered ? 0.6 : 0.35}
              style={{ transition: 'opacity 120ms ease', cursor: 'default' }}
              tabIndex={0}
              role="img"
              aria-label={tipText}
              onMouseEnter={(e) => {
                setHoverLinkIndex(i);
                showTooltip(e, tipText);
              }}
              onMouseMove={(e) => showTooltip(e, tipText)}
              onMouseLeave={() => {
                setHoverLinkIndex(null);
                setTooltip(null);
              }}
              onFocus={(e) => {
                setHoverLinkIndex(i);
                showTooltipAt(e, tipText);
              }}
              onBlur={() => {
                setHoverLinkIndex(null);
                setTooltip(null);
              }}
            />
          );
        })}

        {layout.nodes.map((n) => {
          const tipText = n.tip ?? `${n.label ?? ''}${n.label ? '\n' : ''}${n.subText ?? format(n.value)}${n.cap ? '\n' + n.cap : ''}${n.negligible ? ' (≈0 — rendered at the floor)' : n.floored ? ' (thin flow — rendered at minimum thickness)' : ''}`;
          return (
            <rect
              key={`node-${n.id}`}
              x={n.x0}
              y={n.y0}
              width={n.x1 - n.x0}
              height={n.heightPx}
              rx={2}
              fill={n.striped ? `url(#stripe-${stripeId})` : undefined}
              className={`${n.striped ? '' : n.fillClass} ${FOCUS_RING}`}
              tabIndex={0}
              role="img"
              aria-label={tipText}
              onMouseEnter={(e) => showTooltip(e, tipText)}
              onMouseMove={(e) => showTooltip(e, tipText)}
              onMouseLeave={() => setTooltip(null)}
              onFocus={(e) => showTooltipAt(e, tipText)}
              onBlur={() => setTooltip(null)}
            />
          );
        })}

        {layout.nodes.map((n) => {
          if (n.label == null) return null; // unlabeled mid-column node: tooltip-only
          const lx = n.x1 + 8;
          const ly = n.labelY;
          // SIL-632 — EVERY line of the label block is user-authored free text
          // in some caller (the value line reads "month-0 FMV · acts month 9"
          // as often as it reads a bare number), so all three are truncated to
          // the column's horizontal budget by the layout pass. A line whose
          // budget is too tight for a readable ellipsis is dropped and the
          // ones below it shift up. The untruncated text is always one
          // hover/focus away, same tooltip as the node rect.
          const tipText = n.tip ?? `${n.label}\n${n.subText ?? format(n.value)}${n.cap ? '\n' + n.cap : ''}`;
          const lines = [
            n.labelDisplay && { key: 'label', text: n.labelDisplay, className: 'fill-foreground text-xs font-medium' },
            {
              key: 'value',
              text: `${n.subText == null ? format(n.value) : n.subTextDisplay ?? ''}${n.negligible ? ' ≈0' : ''}`,
              className: 'fill-muted-foreground font-num text-[13px]',
            },
            n.capDisplay && { key: 'cap', text: n.capDisplay, className: 'fill-muted-foreground text-xs' },
          ]
            .filter((l): l is { key: string; text: string; className: string } => Boolean(l) && Boolean((l as { text: string }).text))
            // SIL-633 — same rule vertically: a line the column has no room
            // for is dropped rather than drawn over its neighbour's label.
            .slice(0, n.labelLines);
          return (
            <g
              key={`label-${n.id}`}
              onMouseEnter={(e) => showTooltip(e, tipText)}
              onMouseMove={(e) => showTooltip(e, tipText)}
              onMouseLeave={() => setTooltip(null)}
            >
              {lines.map((line, i) => (
                <text key={line.key} x={lx} y={ly + i * 13} className={line.className}>
                  {line.text}
                </text>
              ))}
            </g>
          );
        })}
      </svg>

      {/* Non-visual equivalent of the diagram's narrative (defect 4): every
          labeled node's value, reachable without hovering/focusing each
          shape individually. */}
      <table className="sr-only">
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th>Node</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {layout.nodes
            .filter((n) => n.label != null)
            .map((n) => (
              <tr key={`sr-node-${n.id}`}>
                <td>
                  {n.label}
                  {n.cap ? ` — ${n.cap}` : ''}
                </td>
                <td>
                  {n.subText ?? format(n.value)}
                  {n.negligible ? ' (≈0)' : ''}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {tooltip && (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-10 max-w-[300px] rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs leading-snug whitespace-pre-line text-popover-foreground shadow-lg"
          style={{ left: tooltip.x + 14, top: tooltip.y + 10 }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
